import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAnnotation,
  deleteAnnotation,
  fetchAnnotations,
  type IDocumentAnnotation,
  type INewAnnotationItem,
} from '@apis/documentAnnotations';

/** Keyed by the file the viewer opened — the id the page has in its URL. */
export const annotationsQueryKey = (siteId: string, fileId: string) =>
  ['documents', 'annotations', siteId, fileId] as const;

/** Refetched on focus: someone else may have drawn while the page sat open. */
export function useDocumentAnnotations(siteId: string, fileId: string, enabled = true) {
  return useQuery({
    queryKey: annotationsQueryKey(siteId, fileId),
    queryFn: ({ signal }) => fetchAnnotations(siteId, fileId, signal),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

/** Refetched rather than patched, so the page shows what was stored. */
export function useSaveAnnotation(siteId: string, fileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { note: string; items: INewAnnotationItem[] }) =>
      createAnnotation(siteId, { fileId, ...input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: annotationsQueryKey(siteId, fileId) }),
  });
}

/** Drops the row at once; a failed delete restores it on the refetch. */
export function useDeleteAnnotation(siteId: string, fileId: string) {
  const queryClient = useQueryClient();
  const key = annotationsQueryKey(siteId, fileId);
  return useMutation({
    mutationFn: (annotationId: string) => deleteAnnotation(siteId, annotationId),
    onMutate: (annotationId: string) => {
      const previous = queryClient.getQueryData<IDocumentAnnotation[]>(key);
      queryClient.setQueryData<IDocumentAnnotation[]>(key, (list) =>
        (list ?? []).filter((a) => a.id !== annotationId),
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}
